import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { MapPin, Zap, Settings, Cpu, Wrench, Activity, Calendar, Hash, Gauge, User } from 'lucide-react';

export const ElectricalLocationCard = ({ open, onOpenChange, projectId, location, defaultTab = 'panels' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);

  const fetchData = useCallback(async () => {
    if (!projectId || !location) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/electrical-systems/${projectId}/schedule-overview/${encodeURIComponent(location)}`
      );
      setData(res.data);
    } catch {
      toast.error('Failed to load location details');
    } finally {
      setLoading(false);
    }
  }, [projectId, location]);

  useEffect(() => {
    if (open && location) {
      fetchData();
      setActiveTab(defaultTab);
    }
  }, [open, location, fetchData, defaultTab]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="location-card-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <MapPin className="h-4 w-4 text-blue-600" />
            </div>
            <span className="text-base">{location}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Activity className="h-6 w-6 text-slate-300 animate-pulse" />
          </div>
        ) : data ? (
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* Summary Row */}
            <div className="grid grid-cols-4 gap-2 flex-shrink-0">
              <MiniStat icon={Zap} label="Panels" value={data.summary.panelCount} color="amber" />
              <MiniStat icon={Settings} label="Equipment" value={data.summary.equipmentCount} color="emerald" />
              <MiniStat icon={Cpu} label="Devices" value={data.summary.deviceCount} color="violet" />
              <MiniStat icon={Gauge} label="Wattage" value={data.summary.totalWattage > 0 ? `${data.summary.totalWattage.toLocaleString()} W` : '-'} color="blue" />
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="flex-shrink-0 grid grid-cols-4 w-full" data-testid="location-card-tabs">
                <TabsTrigger value="panels" className="text-xs gap-1"><Zap className="h-3 w-3" />Panels ({data.summary.panelCount})</TabsTrigger>
                <TabsTrigger value="equipment" className="text-xs gap-1"><Settings className="h-3 w-3" />Equipment ({data.summary.equipmentCount})</TabsTrigger>
                <TabsTrigger value="devices" className="text-xs gap-1"><Cpu className="h-3 w-3" />Devices ({data.summary.deviceCount})</TabsTrigger>
                <TabsTrigger value="maintenance" className="text-xs gap-1"><Wrench className="h-3 w-3" />Maintenance</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto mt-3">
                <TabsContent value="panels" className="mt-0">
                  {data.panels.length === 0 ? (
                    <EmptyTab label="panels" />
                  ) : (
                    <div className="space-y-2" data-testid="location-panels-list">
                      {data.panels.map(p => (
                        <div key={p.id} className="border border-slate-200 rounded-lg p-3 text-sm hover:bg-slate-50/50">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-medium text-slate-800">{p.name}</span>
                            <StatusBadge status={p.status} />
                          </div>
                          <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-500">
                            <DetailRow label="Type" value={p.panelType} />
                            <DetailRow label="Voltage" value={p.voltage} />
                            <DetailRow label="Phase" value={p.phase} />
                            <DetailRow label="Manufacturer" value={p.manufacturerName} />
                            <DetailRow label="Main Breaker" value={p.mainBreaker ? `${p.mainBreaker} A` : null} />
                            <DetailRow label="Circuits" value={p.circuits} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="equipment" className="mt-0">
                  {data.equipment.length === 0 ? (
                    <EmptyTab label="equipment" />
                  ) : (
                    <div className="space-y-2" data-testid="location-equipment-list">
                      {data.equipment.map(e => (
                        <div key={e.id} className="border border-slate-200 rounded-lg p-3 text-sm hover:bg-slate-50/50">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-medium text-slate-800">{e.tag}</span>
                            <StatusBadge status={e.status} />
                          </div>
                          <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-500">
                            <DetailRow label="Type" value={e.equipmentType} />
                            <DetailRow label="Manufacturer" value={e.manufacturer} />
                            <DetailRow label="Model" value={e.brandModel} />
                            {e.ratingValue && <DetailRow label="Rating" value={`${e.ratingValue} ${e.ratingUnit || ''}`} />}
                            <DetailRow label="Serial #" value={e.serialNumber} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="devices" className="mt-0">
                  {data.devices.length === 0 ? (
                    <EmptyTab label="devices" />
                  ) : (
                    <div className="space-y-2" data-testid="location-devices-list">
                      {data.devices.map(d => (
                        <div key={d.id} className="border border-slate-200 rounded-lg p-3 text-sm hover:bg-slate-50/50">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-medium text-slate-800">{d.deviceTag}</span>
                            <StatusBadge status={d.status} />
                          </div>
                          <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-500">
                            <DetailRow label="Category" value={d.category} />
                            <DetailRow label="Type" value={d.deviceType} />
                            <DetailRow label="Manufacturer" value={d.manufacturer} />
                            <DetailRow label="Model" value={d.model} />
                            {d.voltage != null && <DetailRow label="Voltage" value={`${d.voltage} V`} />}
                            {d.wattage != null && <DetailRow label="Wattage" value={`${d.wattage} W`} />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="maintenance" className="mt-0">
                  {(!data.maintenance || data.maintenance.length === 0) ? (
                    <EmptyTab label="maintenance records" />
                  ) : (
                    <div className="space-y-2" data-testid="location-maintenance-list">
                      {data.maintenance.map((m, i) => (
                        <div key={m.id || i} className="border border-slate-200 rounded-lg p-3 text-sm hover:bg-slate-50/50">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-medium text-slate-800">{m.action}</span>
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />{m.date}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-500">
                            <DetailRow label="Company" value={m.companyName} />
                            <DetailRow label="Technician" value={m.technician} />
                            {m.notes && <DetailRow label="Notes" value={m.notes} />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const MiniStat = ({ icon: Icon, label, value, color }) => {
  const colors = {
    amber: 'text-amber-600 bg-amber-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    violet: 'text-violet-600 bg-violet-50',
    blue: 'text-blue-600 bg-blue-50',
  };
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg border border-slate-100" data-testid={`location-stat-${label.toLowerCase()}`}>
      <div className={`w-7 h-7 rounded flex items-center justify-center ${colors[color]}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div>
        <div className="text-sm font-bold text-slate-800">{typeof value === 'number' ? value : value}</div>
        <div className="text-[10px] text-slate-400 leading-none">{label}</div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const isActive = status === 'active';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
      isActive ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
    }`}>
      {status || 'active'}
    </span>
  );
};

const DetailRow = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div>
      <span className="text-slate-400">{label}: </span>
      <span className="text-slate-600">{value}</span>
    </div>
  );
};

const EmptyTab = ({ label }) => (
  <div className="py-8 text-center text-sm text-slate-400" data-testid={`location-empty-${label}`}>
    No {label} at this location.
  </div>
);
